import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster, toast } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { PublicAuthProvider } from "./context/PublicAuthContext";
import { ProducerAuthProvider } from "./context/ProducerAuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { AuraProvider } from "./context/AuraContext";
import PageTracker from "./components/PageTracker";
import MobileBottomNav from "./components/MobileBottomNav";
import GlobalCommunityDrawer from "./components/GlobalCommunityDrawer";

// Public Pages
import HomePage from "./pages/HomePage";
import EventsPage from "./pages/EventsPage";
import DJsListingPage from "./pages/DJsListingPage";
import BravxProfilePage from "./pages/BravxProfilePage";
import BravxLeaderboardPage from "./pages/BravxLeaderboardPage";
import AdminBravxPage from "./pages/admin/AdminBravxPage";
import AdminCampaignCreditsPage from "./pages/admin/AdminCampaignCreditsPage";
import EventDetailPage from "./pages/EventDetailPage";
import TopOfTheWeekPage from "./pages/TopOfTheWeekPage";
import InquiryChatPage from "./pages/InquiryChatPage";
import GalleryPage from "./pages/GalleryPage";
import QueuePage from "./pages/QueuePage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import CheckoutFailurePage from "./pages/CheckoutFailurePage";
import MyTicketsPage from "./pages/MyTicketsPage";
import BenefitsPage from "./pages/BenefitsPage";
import TermsPage from "./pages/TermsPage";
import AboutPage from "./pages/AboutPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import WithdrawalPage from "./pages/WithdrawalPage";
import ProducerPublicPage from "./pages/ProducerPublicPage";
import DJPublicPage from "./pages/DJPublicPage";

// User Auth Pages
import UserAuthPage from "./pages/UserAuthPage";
import UserAccountPage from "./pages/UserAccountPage";
import MerchStorePage from "./pages/MerchStorePage";
import MerchOrderStatusPage from "./pages/MerchOrderStatusPage";
import MerchAdminPage from "./pages/admin/MerchAdminPage";
import { MerchCartProvider } from "./context/MerchCartContext";
import { MerchCartDrawer } from "./components/MerchCartDrawer";
import CompleteProfilePage from "./pages/CompleteProfilePage";

// Admin Pages
import LoginPage from "./pages/admin/LoginPage";
import DashboardPage from "./pages/admin/DashboardPage";
import EventsManagerPage from "./pages/admin/EventsManagerPage";
import CreateEventPage from "./pages/admin/CreateEventPage";
import EventDashboardPage from "./pages/admin/EventDashboardPage";
import AdminEventManagePage from "./pages/admin/AdminEventManagePage";
import EventOrdersPage from "./pages/admin/EventOrdersPage";
import EventDiscountsPage from "./pages/admin/EventDiscountsPage";
import EventTiersPage from "./pages/admin/EventTiersPage";
import EventVendorsPage from "./pages/admin/EventVendorsPage";
import EventInvitationsPage from "./pages/admin/EventInvitationsPage";
import EventReportsPage from "./pages/admin/EventReportsPage";
import EventRRPPPage from "./pages/admin/EventRRPPPage";
import SubeventsManagerPage from "./pages/admin/SubeventsManagerPage";
import FilesPage from "./pages/admin/FilesPage";
import UsersPage from "./pages/admin/UsersPage";
import RolesPage from "./pages/admin/RolesPage";
import LogsPage from "./pages/admin/LogsPage";
import SettingsPage from "./pages/admin/SettingsPage";
import TermsEditorPage from "./pages/admin/TermsEditorPage";
import AboutEditorPage from "./pages/admin/AboutEditorPage";
import GalleryManagerPage from "./pages/admin/GalleryManagerPage";
import TicketsAdminPage from "./pages/admin/TicketsAdminPage";
import TicketScannerPage from "./pages/admin/TicketScannerPage";
import SubscriptionsAdminPage from "./pages/admin/SubscriptionsAdminPage";
import PublicUsersAdminPage from "./pages/admin/PublicUsersAdminPage";
import DJApplicationsPage from "./pages/admin/DJApplicationsPage";
import OpenCallsAdminPage from "./pages/admin/OpenCallsAdminPage";
import AurasAdminPage from "./pages/admin/AurasAdminPage";
import CalendarPage from "./pages/admin/CalendarPage";
import NotesPage from "./pages/admin/NotesPage";
import MyProfilePage from "./pages/admin/MyProfilePage";
import MarketingPage from "./pages/admin/MarketingPage";
import AdminCampaignComposePage from "./pages/admin/AdminCampaignComposePage";
import PartnersAdminPage from "./pages/admin/PartnersAdminPage";
import BenefitApprovalPage from "./pages/admin/BenefitApprovalPage";
import ProducersAdminPage from "./pages/admin/ProducersAdminPage";
import ProducerTermsPage from "./pages/admin/ProducerTermsPage";
import FormsAdminPage from "./pages/admin/FormsAdminPage";
import PartnerFormEditorPage from "./pages/admin/PartnerFormEditorPage";
import ProducerFormEditorPage from "./pages/admin/ProducerFormEditorPage";
import PressFormEditorPage from "./pages/admin/PressFormEditorPage";
import SettlementsPage from "./pages/admin/SettlementsPage";
import ProducerVerificationPage from "./pages/admin/ProducerVerificationPage";
import WithdrawalAdminPage from "./pages/admin/WithdrawalAdminPage";
import FeaturedEventsPage from "./pages/admin/FeaturedEventsPage";
import PartnerPortalPage from "./pages/PartnerPortalPage";
import PartnerApplicationPage from "./pages/PartnerApplicationPage";
import PartnerDashboardPageOld from "./pages/PartnerDashboardPage";
import CreateBenefitPage from "./pages/partner/CreateBenefitPage";

// New Partner Portal Pages
import PartnerDashboardPage from "./pages/partner/PartnerDashboardPage";
import PartnerVerifyPage from "./pages/partner/PartnerVerifyPage";
import PartnerBenefitsPage from "./pages/partner/PartnerBenefitsPage";
import PartnerProfilePage from "./pages/partner/PartnerProfilePage";
import PartnerClientsPage from "./pages/partner/PartnerClientsPage";

// Producer Pages
import ProducerApplicationPage from "./pages/ProducerApplicationPage";
import ProducerLoginPage from "./pages/producer/ProducerLoginPage";
import ProducerDashboardPage from "./pages/producer/ProducerDashboardPage";
import ProducerEventsPage from "./pages/producer/ProducerEventsPage";
import ProducerInquiriesDashboardPage from "./pages/producer/ProducerInquiriesDashboardPage";
import ProducerCommunityPage from "./pages/producer/ProducerCommunityPage";
import ProducerSettingsPage from "./pages/producer/ProducerSettingsPage";
import ProducerScannerPage from "./pages/producer/ProducerScannerPage";
import ProducerCreateEventPage from "./pages/producer/CreateEventPage";
import EventManagePage from "./pages/producer/EventManagePage";
import ProducerDJsPage from "./pages/producer/ProducerDJsPage";
import ProducerJobsPage from "./pages/producer/ProducerJobsPage";
import ProducerEmailMetricsPage from "./pages/producer/ProducerEmailMetricsPage";
import ProducerPressPage from "./pages/producer/ProducerPressPage";
import PressPaymentResultPage from "./pages/producer/PressPaymentResultPage";
import ProducerValidatorsPage from "./pages/producer/ProducerValidatorsPage";
import ProducerAdminsPage from "./pages/producer/ProducerAdminsPage";
import ValidatorDashboardPage from "./pages/producer/ValidatorDashboardPage";
import ProducerSettlementsPage from "./pages/producer/ProducerSettlementsPage";
import ProducerVenuesPage from "./pages/producer/ProducerVenuesPage";
import SubscriptionSuccessPage from "./pages/producer/SubscriptionSuccessPage";
import SubscriptionComparePage from "./pages/producer/SubscriptionComparePage";

// Validator Pages
import ValidatorLoginPage from "./pages/validator/ValidatorLoginPage";
import ValidatorScannerPage from "./pages/validator/ValidatorScannerPage";

// Press Pages
import PressApplicationPage from "./pages/PressApplicationPage";
import PressLoginPage from "./pages/press/PressLoginPage";
import PressDashboardPage from "./pages/press/PressDashboardPage";
import PressAdminPage from "./pages/admin/PressAdminPage";
import PlatformConfigPage from "./pages/admin/PlatformConfigPage";
import ContractsAdminPage from "./pages/admin/ContractsAdminPage";
import StrikesAdminPage from "./pages/admin/StrikesAdminPage";
import NotFoundPage from "./pages/NotFoundPage";
import Url404HitsPage from "./pages/admin/Url404HitsPage";

// User Portal Pages
import DJProfilePage from "./pages/user/DJProfilePage";
import JobsPortalPage from "./pages/user/JobsPortalPage";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import { SidebarProvider } from "./components/AdminSidebarNew";

import "./App.css";

// Router component to handle OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check for Google OAuth session_id in hash - handle synchronously
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.pathname === '/cuenta' && location.hash?.includes('session_id=')) {
    return <UserAuthPage />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/eventos" element={<EventsPage />} />
      <Route path="/tienda" element={<MerchStorePage />} />
      <Route path="/tienda/compra" element={<MerchOrderStatusPage />} />
      <Route
        path="/admin/merch"
        element={
          <ProtectedRoute>
            <MerchAdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/djs" element={<DJsListingPage />} />
      <Route path="/artistas" element={<DJsListingPage />} />
      {/* BRAVX public profile — supports /u/{username} and /@{username} */}
      <Route path="/u/:username" element={<BravxProfilePage />} />
      <Route path="/@:username" element={<BravxProfilePage />} />
      <Route path="/bravx" element={<BravxLeaderboardPage />} />
      <Route path="/bravx/top" element={<BravxLeaderboardPage />} />
      <Route path="/evento/:id" element={<EventDetailPage />} />
      <Route path="/galeria" element={<GalleryPage />} />
      <Route path="/fullbravx" element={<BenefitsPage />} />
      <Route path="/beneficios" element={<BenefitsPage />} />
      <Route path="/productora/:slug" element={<ProducerPublicPage />} />
      <Route path="/dj/:identifier" element={<DJPublicPage />} />
      <Route path="/agencia/:identifier" element={<DJPublicPage />} />
      <Route path="/agencias" element={<DJsListingPage />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/quienes-somos" element={<AboutPage />} />
      <Route path="/c/top" element={<TopOfTheWeekPage />} />
      <Route path="/consultas/:token" element={<InquiryChatPage />} />
      <Route path="/evento/:eventId/queue" element={<QueuePage />} />
      <Route path="/evento/:eventId/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/:eventId" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/failure" element={<CheckoutFailurePage />} />
      <Route path="/mis-tickets" element={<MyTicketsPage />} />
      <Route path="/arrepentimiento" element={<WithdrawalPage />} />

      {/* Producer Public Routes */}
      <Route path="/productoras/aplicar" element={<ProducerApplicationPage />} />
      <Route path="/productoras/login" element={<ProducerLoginPage />} />
      
      {/* Producer Dashboard Routes */}
      <Route path="/producer/login" element={<ProducerLoginPage />} />
      <Route path="/producer/dashboard" element={<ProducerDashboardPage />} />
      <Route path="/producer/events" element={<ProducerEventsPage />} />
      <Route path="/producer/inquiries" element={<ProducerInquiriesDashboardPage />} />
      <Route path="/producer/events/create" element={<ProducerCreateEventPage />} />
      <Route path="/producer/events/:eventId" element={<EventManagePage />} />
      <Route path="/producer/community" element={<ProducerCommunityPage />} />
      <Route path="/producer/scanner" element={<ProducerScannerPage />} />
      <Route path="/producer/settings" element={<ProducerSettingsPage />} />
      <Route path="/producer/djs" element={<ProducerDJsPage />} />
      <Route path="/producer/jobs" element={<ProducerJobsPage />} />
      <Route path="/producer/email-metrics" element={<ProducerEmailMetricsPage />} />
      <Route path="/producer/press" element={<ProducerPressPage />} />
      <Route path="/producer/press/payment" element={<PressPaymentResultPage />} />
      <Route path="/productora/prensa/pago" element={<PressPaymentResultPage />} />
      <Route path="/producer/admins" element={<ProducerAdminsPage />} />
      <Route path="/producer/validators" element={<ProducerValidatorsPage />} />
      <Route path="/producer/validator-dashboard" element={<ValidatorDashboardPage />} />
      <Route path="/producer/settlements" element={<ProducerSettlementsPage />} />
      <Route path="/producer/venues" element={<ProducerVenuesPage />} />
      <Route path="/producer/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/producer/subscription/compare" element={<SubscriptionComparePage />} />
      <Route path="/producer/subscription" element={<SubscriptionComparePage />} />
      <Route path="/producer/*" element={<ProducerDashboardPage />} />

      {/* Validator Routes */}
      <Route path="/validador/login" element={<ValidatorLoginPage />} />
      <Route path="/validador/scanner" element={<ValidatorScannerPage />} />
      <Route path="/validador" element={<ValidatorLoginPage />} />
      <Route path="/validator/scanner" element={<ValidatorScannerPage />} />

      {/* User Auth Routes */}
      <Route path="/cuenta" element={<UserAuthPage />} />
      <Route path="/iniciar-sesion" element={<UserAuthPage />} />
      <Route path="/mi-cuenta" element={<UserAccountPage />} />
      <Route path="/mi-cuenta/dj" element={<DJProfilePage />} />
      <Route path="/mi-cuenta/trabajo" element={<JobsPortalPage />} />
      <Route path="/portal-dj" element={<DJProfilePage />} />
      <Route path="/bolsa-trabajo" element={<JobsPortalPage />} />
      <Route path="/completar-perfil" element={<CompleteProfilePage />} />
      <Route path="/recuperar-password" element={<ForgotPasswordPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/bravx" element={<AdminBravxPage />} />
      <Route path="/admin/campaign-credits" element={<AdminCampaignCreditsPage />} />
      <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos"
        element={
          <ProtectedRoute>
            <EventsManagerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/crear"
        element={
          <ProtectedRoute>
            <CreateEventPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/subeventos"
        element={
          <ProtectedRoute>
            <SubeventsManagerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/editar/:eventId"
        element={
          <ProtectedRoute>
            <AdminEventManagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId"
        element={
          <ProtectedRoute>
            <EventDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/ordenes"
        element={
          <ProtectedRoute>
            <EventOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/descuentos"
        element={
          <ProtectedRoute>
            <EventDiscountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/tandas"
        element={
          <ProtectedRoute>
            <EventTiersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/vendedores"
        element={
          <ProtectedRoute>
            <EventVendorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/invitaciones"
        element={
          <ProtectedRoute>
            <EventInvitationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/reportes"
        element={
          <ProtectedRoute>
            <EventReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos/:eventId/rrpp"
        element={
          <ProtectedRoute>
            <EventRRPPPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/archivos"
        element={
          <ProtectedRoute>
            <FilesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute>
            <RolesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dj-applications"
        element={
          <ProtectedRoute>
            <DJApplicationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/convocatorias"
        element={
          <ProtectedRoute>
            <OpenCallsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/liquidaciones"
        element={
          <ProtectedRoute>
            <SettlementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute>
            <LogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notas"
        element={
          <ProtectedRoute>
            <NotesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scanner"
        element={
          <ProtectedRoute>
            <TicketScannerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/configuracion"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/terminos"
        element={
          <ProtectedRoute>
            <TermsEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/quienes-somos"
        element={
          <ProtectedRoute>
            <AboutEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/galeria"
        element={
          <ProtectedRoute>
            <GalleryManagerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <ProtectedRoute>
            <TicketsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/suscripciones"
        element={
          <ProtectedRoute>
            <SubscriptionsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios-publicos"
        element={
          <ProtectedRoute>
            <PublicUsersAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/auras"
        element={
          <ProtectedRoute>
            <AurasAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/calendario"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/mi-perfil"
        element={
          <ProtectedRoute>
            <MyProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/marketing"
        element={
          <ProtectedRoute>
            <MarketingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campanas/nueva"
        element={
          <ProtectedRoute>
            <AdminCampaignComposePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute>
            <PartnersAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/aprobar-beneficios"
        element={
          <ProtectedRoute>
            <BenefitApprovalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/productoras"
        element={
          <ProtectedRoute>
            <ProducersAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/terminos-productoras"
        element={
          <ProtectedRoute>
            <ProducerTermsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/formularios"
        element={
          <ProtectedRoute>
            <FormsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/verificacion-productoras"
        element={
          <ProtectedRoute>
            <ProducerVerificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/formulario-partner"
        element={
          <ProtectedRoute>
            <PartnerFormEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/formulario-productora"
        element={
          <ProtectedRoute>
            <ProducerFormEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/formulario-prensa"
        element={
          <ProtectedRoute>
            <PressFormEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/arrepentimiento"
        element={
          <ProtectedRoute>
            <WithdrawalAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos-destacados"
        element={
          <ProtectedRoute>
            <FeaturedEventsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Partner Portal (Public) */}
      <Route path="/partner" element={<PartnerPortalPage />} />
      <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
      <Route path="/partner/verificar" element={<PartnerVerifyPage />} />
      <Route path="/partner/beneficios" element={<PartnerBenefitsPage />} />
      <Route path="/partner/clientes" element={<PartnerClientsPage />} />
      <Route path="/partner/perfil" element={<PartnerProfilePage />} />
      <Route path="/partner/crear-beneficio" element={<CreateBenefitPage />} />
      <Route path="/partner/editar-beneficio/:benefitId" element={<CreateBenefitPage />} />
      <Route path="/quiero-ser-partner" element={<PartnerApplicationPage />} />
      <Route path="/partners/aplicar" element={<PartnerApplicationPage />} />

      {/* Press Portal Routes */}
      <Route path="/prensa/solicitud" element={<PressApplicationPage />} />
      <Route path="/prensa/login" element={<PressLoginPage />} />
      <Route path="/prensa/dashboard" element={<PressDashboardPage />} />
      <Route path="/prensa/*" element={<PressDashboardPage />} />
      
      {/* Press Admin Route */}
      <Route
        path="/admin/prensa"
        element={
          <ProtectedRoute>
            <PressAdminPage />
          </ProtectedRoute>
        }
      />
      
      {/* Platform Config & Policies Route */}
      <Route
        path="/admin/plataforma"
        element={
          <ProtectedRoute>
            <PlatformConfigPage />
          </ProtectedRoute>
        }
      />
      
      {/* Contracts Admin Route */}
      <Route
        path="/admin/contratos"
        element={
          <ProtectedRoute>
            <ContractsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/strikes"
        element={
          <ProtectedRoute>
            <StrikesAdminPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/url-404"
        element={
          <ProtectedRoute>
            <Url404HitsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all 404 - MUST be last */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Surface auth:session-expired events from the api interceptor as a friendly toast.
  useEffect(() => {
    const onExpired = () => {
      toast.error('Tu sesión expiró. Iniciá sesión de nuevo.', { duration: 3500 });
    };
    window.addEventListener('auth:session-expired', onExpired);
    return () => window.removeEventListener('auth:session-expired', onExpired);
  }, []);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <HelmetProvider>
        <AuthProvider>
          <PublicAuthProvider>
            <SiteSettingsProvider>
              <AuraProvider>
                <BrowserRouter>
                  <MerchCartProvider>
                  <ProducerAuthProvider>
                    <PageTracker />
                    <div className="App min-h-screen bg-[#0A0A0A]">
                      <div className="noise-overlay" />
                      <AppRouter />
                      <MobileBottomNav />
                      <GlobalCommunityDrawer />
                      <MerchCartDrawer />
                      <Toaster 
                        position="top-center" 
                        toastOptions={{
                          style: {
                            background: '#121212',
                            border: '1px solid #27272A',
                            color: '#FFFFFF',
                            borderRadius: '0',
                          },
                        }}
                      />
                    </div>
                  </ProducerAuthProvider>
                  </MerchCartProvider>
                </BrowserRouter>
              </AuraProvider>
            </SiteSettingsProvider>
          </PublicAuthProvider>
        </AuthProvider>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
